import {isEmpty, map, uniqueId, omit, reduce} from 'lodash';
import React, {useCallback, useMemo, useState, useEffect} from 'react';
import {Form, Button, Select, Search, Input, DatePicker, Tooltip, Popover, Checkbox} from 'acud';
import {ButtonProps} from 'acud/lib/button';
import locale from 'acud/es/date-picker/locale/zh_CN';
import {DuItemControl, MultiFilterItemControl} from '../../../hooks/biz/table/interface';
import RelationFormItems from '../HooksForm/components/RelationFormItems';
import {OutlinedRefresh, OutlinedClose, OutlinedBceAiSetting, OutlinedSetting} from 'acud-icon';
import './Toolbar.less';

export type TActionType = 'link' | 'ajax' | 'dialog' | 'drawer' | 'custom';

export interface CommandParams {
    config: any;
    payload?: any;
}

export interface BulkItemConfig extends ButtonProps {
    id?: string;
    text?: React.ReactNode;
    skin?: 'primary' | 'text' | 'highlight' | 'enhance' | 'default' | 'danger';
    select?: boolean; // 是否和表格批量选择有关
    actionType?: TActionType;
    link?: string;
    ajax?: object;
    dialog?: object;
    drawer?: object;
    action?: string;
}

export interface BulkProps {
    prefixCls?: string;
    bulkComp?: React.ComponentType<any>;
    bulkConfig?: any[];
    selectedRowKeys?: any[];
    handleCommand?: (commandParams: CommandParams) => void;
    handleFilterChange?: (filterItemData: any) => void;
}

export interface SelectedProps {
    prefixCls?: string;
    selectedRowKeys?: any[];
    total?: number;
}

export interface IFilterConfig {
    controls?: any[];
}

export interface SetColumnsProps {
    disabledColumns?: string[];
}

export interface FilterProps {
    prefixCls?: string;
    filterComp?: React.ComponentType<any>;
    filterConfig?: IFilterConfig;
    isMore20?: boolean;
    setCloumnKeys?: (cloumnsKeyList: any[]) => void;
    cloumnsKeyList?: any[];
    handleTableCommand?: any,
    columns?: any[];
    setColumnsConfig?: SetColumnsProps;
    setColumns?: (columns: any[]) => void;
    handleCommand?: (commandParams: CommandParams) => void;
    handleFilterChange?: (filterItemData: any) => void;
}

export interface ToolbarProps {
    prefixCls?: string;
    withToolbar?: boolean;
    controlsConfig?: {
        labelWidth?: number;
        colsNum?: number;
        controls?: DuItemControl[];
    };
    multiFilterConfig?: {
        controls?: MultiFilterItemControl[];
    };
    bulkProps?: BulkProps;
    selectedProps?: SelectedProps;
    filterProps?: FilterProps;
    handleCommand?: (commandParams: CommandParams) => void;
    handleFilterChange?: (filterItemData: any) => void;
    setColumnsConfig?: SetColumnsProps;
    setColumns?: (columns: any[]) => void;
    columns?: any[];
    containerWidth?: number;
}

export const RenderSetColumns = (props: any) => {
    const {columns = [], setColumns, setColumnsConfig} = props;
    const [visible, setVisible] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [checkedList, setCheckedList] = useState<string[]>([]);
    const [indeterminate, setIndeterminate] = useState(false);
    const [checkAll, setCheckAll] = useState(true);

    // 初始化时设置默认选中的列
    useEffect(() => {
        const defaultCheckedList = columns
            .filter((col: any) => !col.hidden)
            .map((col: any) => col.dataIndex);
        setCheckedList(defaultCheckedList);
    }, [columns]);

    const filteredColumns = useMemo(() => {
        return columns.map((column: any) => {
            const isDisplay = column.title?.toString().toLowerCase().includes(searchValue.toLowerCase());
            return {...column, isDisplay};
        }
        );
    }, [columns, searchValue]);

    // 更新表格列显示状态
    const updateTableColumns = (checkedValues: any[]) => {
        const newColumns = columns.map((col: any) => ({
            ...col,
            hidden: !checkedValues.includes(col.dataIndex)
        }));
        setColumns(newColumns);
    };

    const onCheckAllChange = (e: any) => {
        const newCheckedList = e.target.checked
            ? filteredColumns.map((col: any) => col.dataIndex)
            : setColumnsConfig?.disabledColumns || [];
        setIndeterminate(false);
        setCheckAll(e.target.checked);
        setCheckedList(newCheckedList);
        updateTableColumns(newCheckedList);
    };

    const onCheckChange = (checkedValues: any[]) => {
        let targetCheckedValues = checkedValues;
        setCheckedList(targetCheckedValues);
        setIndeterminate(!!targetCheckedValues.length && targetCheckedValues.length < filteredColumns.length);
        setCheckAll(targetCheckedValues.length === filteredColumns.length);
        updateTableColumns(targetCheckedValues);
    };

    const handleVisibleChange = (flag: boolean) => {
        setVisible(flag);
    };

    const content = (
        <div className="set-columns-popover">
            <div className="search-input">
                <Search
                    placeholder="搜索列"
                    value={searchValue}
                    onChange={(e: any) => setSearchValue(e.target.value)}
                />
            </div>
            <div className="check-all">
                {!searchValue ? (
                    <Checkbox
                        indeterminate={indeterminate}
                        onChange={onCheckAllChange}
                        checked={checkAll}
                    >
                        全选
                    </Checkbox>) : null}
            </div>
            <div className="check-group">
                <Checkbox.Group
                    value={checkedList}
                    onChange={onCheckChange}
                >
                    {filteredColumns.map((column: any) => (
                        <div
                            key={column.dataIndex}
                            style={{padding: '8px 12px', display: column.isDisplay ? 'block' : 'none'}}
                        >
                            <Checkbox
                                value={column.dataIndex}
                                disabled={setColumnsConfig?.disabledColumns?.includes(column.dataIndex)}
                            >
                                {column.title}
                            </Checkbox>
                        </div>
                    ))}
                </Checkbox.Group>
            </div>
        </div>
    );

    return (
        <Popover
            content={content}
            trigger="click"
            visible={visible}
            placement="bottomLeft"
            onVisibleChange={handleVisibleChange}
        >
            <Button className={`set-columns ${visible ? 'active' : ''}`} type="text" icon={<OutlinedSetting />} />
        </Popover>
    );
};

export const renderButton = (props: any) => {
    const {disabled, skin, text, handleCommand, customRender, ...buttonConfig} = props;
    const type = skin === 'danger' ? 'highlight' : skin;

    const btnPanel = (
        <Button
            key={uniqueId()}
            type={type}
            disabled={disabled}
            {...omit(buttonConfig, ['actionType', 'select'])}
            onClick={() => handleCommand?.({config: props})}
        >
            {text}
        </Button>
    );
    return customRender?.(btnPanel, props) || btnPanel;
};

const Bulk = (props: BulkProps) => {
    const {prefixCls, bulkComp: BulkComp, bulkConfig, selectedRowKeys, handleCommand, handleFilterChange} = props;

    const hasSelected = (selectedRowKeys ?? []).length > 0;

    if (BulkComp) {
        return (
            <BulkComp
                selectedRowKeys={selectedRowKeys}
                hasSelected={hasSelected}
                handleCommand={handleCommand}
                handleFilterChange={handleFilterChange}
            />
        );
    }

    return (
        <div className={`${prefixCls}-hooks-table-toolbar-bulk`}>
            {isEmpty(bulkConfig) ? null : map(bulkConfig, configItem => {
                const disabled = configItem.disabled || (configItem.select && !hasSelected);
                return renderButton({...configItem, disabled, handleCommand});
            })}
        </div>
    );
};

const Selected = (props: SelectedProps) => {
    const {prefixCls, selectedRowKeys, total} = props;

    const selected = (selectedRowKeys ?? []).length;

    if (!selected) {
        return <></>;
    }
    return (
        <span className={`${prefixCls}-hooks-table-selected-count`}>
            已选中 {selected} 条，共 {total} 条
        </span>
    );
};

const Filter = (props: FilterProps) => {
    const {
        prefixCls, filterComp: FilterComp, filterConfig, isMore20, cloumnsKeyList,
        columns, setColumnsConfig, handleTableCommand,
        setColumns, setCloumnKeys, handleCommand, handleFilterChange
    } = props;

    const attributeDom = isMore20 ? (
        <Button
            style={{paddingRight: 6, paddingLeft: 8}}
            key={uniqueId()}
            onClick={() => handleTableCommand?.('ATTRIBUTES', {cloumnKeys: cloumnsKeyList, setCloumnKeys})}
        >
            <OutlinedBceAiSetting style={{fontSize: 16}} />
        </Button>
    ) : null;

    if (FilterComp) {
        return isMore20 ? (
            <>
                <FilterComp handleFilterChange={handleFilterChange} />
                {attributeDom}
            </>
        ) : <FilterComp handleFilterChange={handleFilterChange} />;
    }

    const controls = filterConfig?.controls;

    if (!isMore20 && (isEmpty(filterConfig) || isEmpty(controls))) {
        return null;
    }

    return (
        <div className={`${prefixCls}-hooks-table-toolbar-filter`}>
            {map(controls, (controlItem, idx) => {
                if (controlItem.actionType) {
                    return renderButton({...controlItem, handleCommand});
                }
                const {label, control: Control, controlProps} = controlItem;
                return Control ? (
                    <div key={idx}>
                        {label && <label>{label}</label>}
                        <Control {...controlProps} />
                    </div>
                ) : null;
            })}
            {attributeDom}
            {setColumnsConfig ? <RenderSetColumns
                setColumnsConfig={setColumnsConfig}
                handleCommand={handleCommand}
                columns={columns}
                setColumns={setColumns}
            /> : ''}
        </div>
    );
};

const {RangePicker} = DatePicker;
const MultiFilter = props => {
    const {controls, handleFilterChange, prefixCls} = props;
    const [selectValue, setSelectValue] = useState(props.controls[0].value); // 多维检索中当前选中的检索项
    const [allSearchArr, setAllSearchArr] = useState<any>([]); // 当前所有搜索项，用于展示遍历

    const onSearchSelectChange = val => {
        setSelectValue(val);
    };

    // 刷新按钮
    const refresh = () => {
        handleFilterChange({...allSearchArr, multiply: true});
    };

    // 全部清空搜索条件
    const handleClearAllSearch = () => {
        const arr = allSearchArr.map(item => ({...item, value: null}));
        handleFilterChange({...arr, multiply: true});
        setAllSearchArr([]);
    };

    // 清空单个搜索条件
    const handleClearSearch = key => {
        const arr = allSearchArr.map(item => ({...item, value: item.key === key ? null : item.value}));
        setAllSearchArr([...arr]);
        handleFilterChange({...arr, multiply: true});
    };

    // 点击搜索图标或者change事件改变时触发搜索
    const handleSearch = value => {
        if (!value || value.length === 0) {
            const obj = controls.find(item => item.value === selectValue);
            const newSearchArr = [
                ...allSearchArr.filter(item => item.key !== obj?.label),
                {
                    key: obj?.label,
                    value: null,
                    type: obj?.type,
                    searchParam: obj?.value
                }
            ];
            setAllSearchArr([...newSearchArr]);
            handleFilterChange({...newSearchArr, multiply: true});
            return;
        }
        const obj = controls.find(item => item.value === selectValue);
        const newSearchArr = [
            ...allSearchArr.filter(item => item.key !== obj?.label),
            {
                key: obj?.label,
                value:
                    obj?.type === 'rangePicker'
                        ? [value[0].format('YYYY-MM-DD HH:mm:ss'), value[1].format('YYYY-MM-DD HH:mm:ss')]
                        : obj?.type === 'time'
                            ? value.format('YYYY-MM-DD HH:mm:ss')
                            : value,
                type: obj?.type,
                searchParam: obj?.value
            }
        ];
        setAllSearchArr([...newSearchArr]);
        handleFilterChange({...newSearchArr, multiply: true});
    };

    return (
        <div className={`${prefixCls}-filter-multi`}>
            <div className="filter-content">
                <div></div>
                <div className="filter-right">
                    <Select
                        defaultValue={controls[0].value}
                        options={controls}
                        onChange={onSearchSelectChange}
                    />
                    {controls
                        .filter(val => val.value === selectValue)
                        .map(item => {
                            let component;
                            switch (item.type) {
                                case 'input':
                                    component = <Search allowClear onSearch={handleSearch} key={item.value} />;
                                    break;
                                case 'select':
                                    component = (
                                        <Input.AutoComplete key={item.value} options={item.config.options}>
                                            <Search allowClear onSearch={handleSearch} />
                                        </Input.AutoComplete>
                                    );
                                    break;
                                case 'rangePicker':
                                    component = (
                                        <RangePicker
                                            format="YYYY-MM-DD HH:mm:ss"
                                            locale={locale}
                                            style={{width: 260}}
                                            allowClear
                                            key={item.value}
                                            showTime
                                            onChange={handleSearch}
                                        />
                                    );
                                    break;
                                case 'time':
                                    component = (
                                        <DatePicker
                                            format="YYYY-MM-DD HH:mm:ss"
                                            locale={locale}
                                            style={{width: 260}}
                                            allowClear
                                            key="date"
                                            showTime
                                            onChange={handleSearch}
                                        />
                                    );
                                    break;
                                default:
                                    component = null;
                            }
                            return component;
                        })}

                    <Button className="refresh" onClick={refresh}>
                        <OutlinedRefresh />
                    </Button>
                </div>
            </div>

            <div className="filter-content">
                <div style={{flex: 1}}></div>
                {allSearchArr.filter(item => !!item.value || item.value === 0).map(item => {
                    return (
                        <Tooltip title={`${item.key}: ${item.value}`} className="filter-item" key={item.key}>
                            <span>{item.value && `${item.key}：${item.value}`}</span>
                            <OutlinedClose
                                onClick={() => handleClearSearch(item.key)}
                                style={{
                                    marginLeft: '3px',
                                    color: '#2468f2',
                                    cursor: 'pointer'
                                }}
                            />
                        </Tooltip>
                    );
                })}
                {allSearchArr.filter(item => !!item.value || item.value === 0).length > 0 ? (
                    <span className="clear-all" onClick={handleClearAllSearch}>
                        全部清除
                    </span>
                ) : (
                    ''
                )}
            </div>
        </div>
    );
};

const ToolBar = (props: ToolbarProps) => {
    const {
        prefixCls,
        withToolbar,
        controlsConfig = {},
        bulkProps,
        multiFilterConfig,
        selectedProps,
        filterProps,
        columns,
        setColumnsConfig,
        setColumns,
        handleCommand,
        handleFilterChange,
        containerWidth = 0
    } = props;

    const defaultCheckedList = (controlsConfig?.controls || []).reduce((res, item) => {
        if (!item?.unShow) {
            res.push(item?.label);
        }
        return res;
    }, [] as string[]);
    const [showQueryNames, setShowQueryNames] = useState<string[]>(defaultCheckedList);
    const [form] = Form.useForm();
    const formData = useMemo(() => {
        if (controlsConfig?.controls) {
            return reduce(controlsConfig?.controls, (controlRes, controlItem) => {
                if (controlItem.name && controlItem.defaultValue) {
                    controlRes[controlItem.name] = controlItem.defaultValue;
                }
                return controlRes;
            }, {});
        }
        return {};
    }, [controlsConfig]);

    const defaultParams = useMemo(() => {
        return (controlsConfig?.controls || []).reduce((res, item) => {
            res[item.name] = undefined;
            return res;
        }, {} as {[key: string]: undefined});
    }, [controlsConfig]);

    const doSubmit = useCallback((values: any) => {
        if (handleFilterChange) {
            handleFilterChange({...defaultParams, ...values});
        }
    }, [handleFilterChange, defaultParams]);
    const onReset = useCallback(() => {
        form.resetFields();
    }, [form]);
    const handleControlsChange = useCallback((_, allValues) => {
        if (showQueryNames.length < 2) {
            if (handleFilterChange) {
                handleFilterChange({...defaultParams, ...allValues});
            }
        }
    }, [showQueryNames, defaultParams, handleFilterChange]);

    const cols = useMemo(() => {
        return controlsConfig?.colsNum ? controlsConfig?.colsNum : Math.floor(containerWidth / 312);
    }, [containerWidth, controlsConfig?.colsNum]);

    return withToolbar ? (
        <>
            {
                !isEmpty(controlsConfig) ? (
                    <Form
                        form={form}
                        layout="horizontal"
                        name="basic"
                        labelAlign='left'
                        labelWidth={controlsConfig?.labelWidth || 72}
                        cols={cols}
                        initialValues={formData}
                        onFinish={doSubmit}
                        {
                            ...showQueryNames.length === 1 ? {
                                onValuesChange: handleControlsChange
                            } : {}
                        }
                    >
                        <RelationFormItems
                            prefixCls={prefixCls}
                            formData={formData}
                            controls={((controlsConfig?.controls || []) as DuItemControl[])}
                            cols={cols}
                            isHasQueryPanel
                            defaultCheckedList={defaultCheckedList}
                            onReset={onReset}
                            showQueryNames={showQueryNames}
                            setShowQueryNames={setShowQueryNames}
                        />
                    </Form>
                ) : null
            }
            <div className={`${prefixCls}-hooks-table-toolbar`}>
                <div className={`${prefixCls}-hooks-table-toolbar-left`}>
                    <Bulk
                        prefixCls={prefixCls}
                        {...bulkProps}
                        selectedRowKeys={selectedProps?.selectedRowKeys}
                        handleCommand={handleCommand}
                        handleFilterChange={handleFilterChange}
                    />
                    <Selected prefixCls={prefixCls} {...selectedProps} />
                </div>
                <div className={`${prefixCls}-hooks-table-toolbar-right`}>
                    {multiFilterConfig
                        && <MultiFilter
                            prefixCls={prefixCls}
                            {...multiFilterConfig}
                            handleFilterChange={handleFilterChange}
                        />
                    }
                    <Filter
                        prefixCls={prefixCls}
                        {...filterProps}
                        columns={columns}
                        setColumnsConfig={setColumnsConfig}
                        setColumns={setColumns}
                        handleCommand={handleCommand}
                        handleFilterChange={handleFilterChange}
                    />
                </div>

            </div>
        </>
    ) : null;
};

export default ToolBar;
